// src/lib/clients.ts

// 既存のインポートに追加
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
  getCountFromServer,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Client, Contact, ClientWithContacts } from '@/types';

/**
 * 🆕 アクティブなクライアント数を取得
 */
export const getActiveClientCount = async (userId: string): Promise<number> => {
  const q = query(
    collection(db, 'users', userId, 'clients'),
    where('status', '==', 'active')
  );
  
  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
};

/**
 * 🆕 クライアントの担当者数を取得
 */
export const getContactCountByClient = async (
  userId: string,
  clientId: string
): Promise<number> => {
  const q = query(
    collection(db, 'users', userId, 'contacts'),
    where('clientId', '==', clientId)
  );
  
  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
};

/**
 * クライアント一覧をリアルタイム購読（ステータスフィルター対応）
 */
export const subscribeToClients = (
  userId: string,
  callback: (clients: Client[]) => void,
  status?: 'active' | 'archived' | 'all'
) => {
  let q;
  
  if (status && status !== 'all') {
    q = query(
      collection(db, 'users', userId, 'clients'),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
  } else {
    q = query(
      collection(db, 'users', userId, 'clients'),
      orderBy('createdAt', 'desc')
    );
  }

  return onSnapshot(q, (snapshot) => {
    const clients = snapshot.docs.map((doc) => ({
      id: doc.id,
      status: 'active', // デフォルト値（既存データ対応）
      ...doc.data(),
    })) as Client[];
    callback(clients);
  });
};

/**
 * 担当者一覧をリアルタイム購読
 */
export const subscribeToContacts = (
  userId: string,
  callback: (contacts: Contact[]) => void
) => {
  const q = query(
    collection(db, 'users', userId, 'contacts'),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const contacts = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Contact[];
    callback(contacts);
  });
};

/**
 * クライアントと担当者を結合してリアルタイム購読
 */
export const subscribeToClientsWithContacts = (
  userId: string,
  callback: (clientsWithContacts: ClientWithContacts[]) => void,
  status?: 'active' | 'archived' | 'all'
) => {
  let clients: Client[] = [];
  let contacts: Contact[] = [];
  let isInitialized = { clients: false, contacts: false };

  const updateClientsWithContacts = () => {
    if (!isInitialized.clients || !isInitialized.contacts) {
      return;
    }

    const clientsWithContacts: ClientWithContacts[] = clients.map((client) => ({
      ...client,
      contacts: contacts.filter((contact) => contact.clientId === client.id),
    }));
    
    callback(clientsWithContacts);
  };

  const clientsUnsub = subscribeToClients(userId, (updatedClients) => {
    clients = updatedClients;
    isInitialized.clients = true;
    updateClientsWithContacts();
  }, status);

  const contactsUnsub = subscribeToContacts(userId, (updatedContacts) => {
    contacts = updatedContacts;
    isInitialized.contacts = true;
    updateClientsWithContacts();
  });

  return () => {
    clientsUnsub();
    contactsUnsub();
  };
};

/**
 * クライアントを追加（🆕 status フィールド追加）
 */
export const addClient = async (userId: string, data: Partial<Client>) => {
  const clientData = {
    userId,
    name: data.name || '',
    email: data.email || '',
    phone: data.phone || '',
    address: data.address || '',
    notes: data.notes || '',
    status: data.status || 'active',  // 🆕 デフォルトは active
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, 'users', userId, 'clients'), clientData);
  return docRef.id;
};

/**
 * クライアントを更新
 */
export const updateClient = async (
  userId: string,
  clientId: string,
  data: Partial<Client>
) => {
  const clientRef = doc(db, 'users', userId, 'clients', clientId);
  await updateDoc(clientRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

/**
 * クライアントを削除
 */
export const deleteClient = async (userId: string, clientId: string) => {
  const clientRef = doc(db, 'users', userId, 'clients', clientId);
  await deleteDoc(clientRef);
};

/**
 * 🆕 クライアントをアーカイブ
 */
export const archiveClient = async (userId: string, clientId: string) => {
  const clientRef = doc(db, 'users', userId, 'clients', clientId);
  await updateDoc(clientRef, {
    status: 'archived',
    updatedAt: serverTimestamp(),
  });
};

/**
 * 🆕 クライアントをアクティブに戻す
 */
export const activateClient = async (userId: string, clientId: string) => {
  const clientRef = doc(db, 'users', userId, 'clients', clientId);
  await updateDoc(clientRef, {
    status: 'active',
    updatedAt: serverTimestamp(),
  });
};

/**
 * 担当者を追加
 */
export const addContact = async (userId: string, data: Partial<Contact>) => {
  const contactData = {
    userId,
    clientId: data.clientId || '',
    name: data.name || '',
    nameKana: data.nameKana || '',
    nameEn: data.nameEn || '',
    email: data.email || '',
    phone: data.phone || '',
    department: data.department || '',
    title: data.title || '',
    memo: data.memo || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, 'users', userId, 'contacts'), contactData);
  return docRef.id;
};

/**
 * 担当者を更新
 */
export const updateContact = async (
  userId: string,
  contactId: string,
  data: Partial<Contact>
) => {
  const contactRef = doc(db, 'users', userId, 'contacts', contactId);
  await updateDoc(contactRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

/**
 * 担当者を削除
 */
export const deleteContact = async (userId: string, contactId: string) => {
  const contactRef = doc(db, 'users', userId, 'contacts', contactId);
  await deleteDoc(contactRef);
};

/**
 * クライアントに紐づく担当者を取得
 */
export const getContactsByClient = async (
  userId: string,
  clientId: string
): Promise<Contact[]> => {
  const q = query(
    collection(db, 'users', userId, 'contacts'),
    where('clientId', '==', clientId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Contact[];
};
